import { useState, useEffect, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { X, Clock, Users, CheckCircle2, Flame, Zap, Share2, ExternalLink } from 'lucide-react';
import { ShareToNostrDialog } from '@/components/ShareToNostrDialog';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePollResults } from '@/hooks/usePollResults';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import type { Poll } from '@/hooks/usePolls';
import { genUserName } from '@/lib/genUserName';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/useToast';

// Animated number component with smooth transitions
function AnimatedNumber({ value, className }: { value: number; className?: string }) {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      const startValue = prevValue.current;
      const endValue = value;
      const duration = 600;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(startValue + (endValue - startValue) * easeOut);
        
        setDisplayValue(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
      prevValue.current = value;
    }
  }, [value]);

  return <span className={className}>{displayValue}</span>;
}

// Animated percentage with smooth transitions
function AnimatedPercentage({ value }: { value: number }) {
  const [displayValue, setDisplayValue] = useState(value);
  const prevValue = useRef(value);

  useEffect(() => {
    if (value !== prevValue.current) {
      const startValue = prevValue.current;
      const endValue = value;
      const duration = 800;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const current = startValue + (endValue - startValue) * easeOut;
        
        setDisplayValue(current);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
      prevValue.current = value;
    }
  }, [value]);

  return (
    <span className="font-black text-4xl md:text-5xl tabular-nums bg-gradient-to-r from-orange-400 via-amber-400 to-orange-500 bg-clip-text text-transparent">
      {displayValue.toFixed(1)}%
    </span>
  );
}

// Voter avatar in fullscreen mode
function VoterAvatar({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name || metadata?.display_name || genUserName(pubkey);
  const avatarUrl = metadata?.picture;

  return (
    <Tooltip>
      <TooltipTrigger>
        <Avatar className="h-8 w-8 md:h-10 md:w-10 ring-2 ring-orange-500/30 border-2 border-black/50 hover:ring-orange-500 hover:scale-110 transition-all duration-300 cursor-pointer">
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback className="text-xs bg-gradient-to-br from-orange-500/30 to-amber-500/30 font-bold text-orange-400">
            {displayName[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </TooltipTrigger>
      <TooltipContent className="bg-black/90 border border-orange-500/30 text-white">
        <p className="font-bold">{displayName}</p>
      </TooltipContent>
    </Tooltip>
  );
}

interface FullScreenPollProps {
  poll: Poll;
  onClose: () => void;
}

export function FullScreenPoll({ poll, onClose }: FullScreenPollProps) {
  const author = useAuthor(poll.pubkey);
  const { user } = useCurrentUser();
  const results = usePollResults(poll, user?.pubkey);
  const { mutate: publishVote, isPending: isVoting } = useNostrPublish();
  const { toast } = useToast();

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [showVoteRipple, setShowVoteRipple] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || metadata?.display_name || genUserName(poll.pubkey);
  const avatarUrl = metadata?.picture;

  const isPollEnded = poll.endsAt ? Date.now() / 1000 > poll.endsAt : false;
  const userHasVoted = !!results.data?.userVote || hasVoted;
  const shouldShowResults = userHasVoted || isPollEnded;
  const totalVotes = results.data?.totalVotes || 0;

  // Track data updates for live indicator
  useEffect(() => {
    if (results.data) {
      setLastUpdateTime(Date.now());
    }
  }, [results.data?.totalVotes]);

  // Auto-submit vote when option is selected (for single choice)
  useEffect(() => {
    if (poll.pollType === 'singlechoice' && selectedOptions.length === 1 && !userHasVoted && !isVoting && user) {
      const timeoutId = setTimeout(() => {
        handleVote();
      }, 400);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedOptions, poll.pollType, userHasVoted, isVoting, user]);

  const handleOptionToggle = (optionId: string) => {
    if (userHasVoted || isPollEnded || isVoting) return;

    setShowVoteRipple(optionId);
    setTimeout(() => setShowVoteRipple(null), 700);

    if (poll.pollType === 'singlechoice') {
      setSelectedOptions([optionId]);
    } else {
      setSelectedOptions(prev =>
        prev.includes(optionId)
          ? prev.filter(id => id !== optionId)
          : [...prev, optionId]
      );
    }
  };

  const handleVote = () => {
    if (!user) {
      toast({
        title: 'Login required',
        description: 'Please login to vote on polls',
        variant: 'destructive',
      });
      return;
    }

    if (selectedOptions.length === 0) {
      toast({
        title: 'Select an option',
        description: 'Please select at least one option to vote',
        variant: 'destructive',
      });
      return;
    }

    const tags: string[][] = [['e', poll.id]];
    selectedOptions.forEach(optionId => {
      tags.push(['response', optionId]);
    });

    publishVote(
      {
        kind: 1018,
        content: '',
        tags,
      },
      {
        onSuccess: () => {
          setHasVoted(true);
          toast({
            title: '🔥 Vote submitted!',
            description: 'Your vote has been recorded',
          });
        },
        onError: () => {
          toast({
            title: 'Vote failed',
            description: 'Failed to submit your vote. Please try again.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const getPollUrl = () => `${window.location.origin}?poll=${poll.id}`;

  const handleShare = async () => {
    const url = getPollUrl();
    try {
      if (navigator.share) {
        await navigator.share({
          title: poll.question,
          text: `Cast your vote! ${poll.question}`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: 'Link copied!',
          description: 'Poll link has been copied to clipboard',
        });
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          toast({ title: 'Link copied!', description: 'Poll link copied to clipboard' });
        } catch {
          toast({ title: 'Share failed', variant: 'destructive' });
        }
      }
    }
  };

  // Find leading option
  const leadingOption = poll.options.reduce((max, opt) => {
    const count = results.data?.optionCounts[opt.id] || 0;
    const maxCount = results.data?.optionCounts[max.id] || 0;
    return count > maxCount ? opt : max;
  }, poll.options[0]);

  return (
    <div className="fixed inset-0 z-[100] bg-black overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Dark gradient base */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black" />
        
        {/* Animated ember orbs */}
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-orange-500/20 rounded-full blur-[150px] animate-float-slow" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-amber-500/15 rounded-full blur-[120px] animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-600/10 rounded-full blur-[200px] animate-breathe" />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,140,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,140,0,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-4 md:p-6">
          <div className="flex items-center justify-between">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-12 w-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/30 transition-all duration-300 group"
            >
              <X className="h-6 w-6 text-white/70 group-hover:text-white transition-colors" />
            </Button>

            {/* Live Indicator */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30">
                <div className="relative">
                  <div className="h-2.5 w-2.5 bg-orange-500 rounded-full animate-pulse" />
                  <div className="absolute inset-0 h-2.5 w-2.5 bg-orange-500 rounded-full animate-ping" />
                </div>
                <span className="text-sm font-bold text-orange-400">LIVE</span>
              </div>
              
              {/* Share buttons */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="h-12 w-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/30 transition-all duration-300"
              >
                <Share2 className="h-5 w-5 text-white/70" />
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-8">
          <div className="max-w-3xl mx-auto space-y-8">
            {/* Author Section */}
            <div className="flex flex-col items-center text-center space-y-4 animate-slide-up">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full blur-xl opacity-50 group-hover:opacity-80 transition-opacity duration-500 animate-breathe" />
                <Avatar className="h-24 w-24 md:h-32 md:w-32 ring-4 ring-orange-500/50 border-4 border-black relative transition-transform duration-300 group-hover:scale-105">
                  <AvatarImage src={avatarUrl} alt={displayName} />
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-amber-600 text-white text-3xl md:text-4xl font-black">
                    {displayName[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="space-y-1">
                <h2 className="text-xl md:text-2xl font-bold text-white">{displayName}</h2>
                <p className="text-white/50 text-sm">
                  {formatDistanceToNow(poll.created_at * 1000, { addSuffix: true })}
                </p>
              </div>

              {/* Badges */}
              <div className="flex gap-2 flex-wrap justify-center">
                {poll.pollType === 'multiplechoice' && (
                  <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 font-bold">
                    Multi-choice
                  </Badge>
                )}
                {isPollEnded && (
                  <Badge className="bg-white/10 text-white/70 border-white/20 font-bold">
                    Ended
                  </Badge>
                )}
                {poll.endsAt && !isPollEnded && (
                  <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 font-bold flex items-center gap-1.5">
                    <Clock className="h-3 w-3" />
                    Ends {formatDistanceToNow(poll.endsAt * 1000, { addSuffix: true })}
                  </Badge>
                )}
              </div>
            </div>

            {/* Question */}
            <div className="text-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                {poll.question}
              </h1>
            </div>

            {/* Vote Count */}
            <div className="flex justify-center animate-slide-up" style={{ animationDelay: '0.15s' }}>
              <div className="flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/5 border border-white/10">
                <Users className="h-5 w-5 text-orange-500" />
                <span className="text-xl font-bold text-white tabular-nums">
                  <AnimatedNumber value={totalVotes} /> {totalVotes === 1 ? 'vote' : 'votes'}
                </span>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4">
              {poll.options.map((option, index) => {
                const voteCount = results.data?.optionCounts[option.id] || 0;
                const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
                const isSelected = selectedOptions.includes(option.id);
                const isUserVote = results.data?.userVote?.responseIds.includes(option.id);
                const isRippling = showVoteRipple === option.id;
                const isLeading = leadingOption.id === option.id && totalVotes > 0;

                const votersForOption = results.data?.votes.filter(vote =>
                  vote.responseIds.includes(option.id)
                ) || [];

                return (
                  <div
                    key={option.id}
                    className="animate-slide-up-bold"
                    style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                  >
                    {shouldShowResults ? (
                      // Results View
                      <div className={`
                        relative p-5 md:p-6 rounded-2xl border-2 transition-all duration-500
                        ${isLeading 
                          ? 'bg-gradient-to-r from-orange-500/20 via-amber-500/15 to-orange-500/20 border-orange-500/50 shadow-lg shadow-orange-500/20' 
                          : 'bg-white/5 border-white/10'
                        }
                      `}>
                        {/* Progress bar background */}
                        <div className="absolute inset-0 rounded-2xl overflow-hidden">
                          <div
                            className={`h-full transition-all duration-1000 ease-out ${
                              isLeading 
                                ? 'bg-gradient-to-r from-orange-500/30 via-amber-500/20 to-orange-500/30' 
                                : 'bg-white/5'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>

                        {/* Content */}
                        <div className="relative z-10 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {isLeading && totalVotes > 0 && (
                              <Flame className="h-6 w-6 text-orange-500 animate-fire-dance flex-shrink-0" />
                            )}
                            <span className="font-bold text-lg md:text-xl text-white truncate">
                              {option.label}
                            </span>
                            {isUserVote && (
                              <div className="flex items-center gap-1.5 bg-orange-500/30 px-3 py-1 rounded-full border border-orange-500/50 flex-shrink-0">
                                <CheckCircle2 className="h-4 w-4 text-orange-400" />
                                <span className="text-sm font-black text-orange-400">You</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 flex-shrink-0">
                            <span className="text-white/50 font-bold tabular-nums">
                              <AnimatedNumber value={voteCount} />
                            </span>
                            <AnimatedPercentage value={percentage} />
                          </div>
                        </div>

                        {/* Voters */}
                        {votersForOption.length > 0 && (
                          <div className="relative z-10 mt-4 flex items-center gap-1">
                            <TooltipProvider>
                              {votersForOption.slice(0, 10).map((vote, i) => (
                                <div key={vote.pubkey} className="animate-scale-in" style={{ animationDelay: `${i * 0.05}s` }}>
                                  <VoterAvatar pubkey={vote.pubkey} />
                                </div>
                              ))}
                              {votersForOption.length > 10 && (
                                <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center text-xs font-bold text-white/70">
                                  +{votersForOption.length - 10}
                                </div>
                              )}
                            </TooltipProvider>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Voting View
                      <button
                        onClick={() => handleOptionToggle(option.id)}
                        disabled={isPollEnded || userHasVoted || isVoting || !user}
                        className={`
                          w-full relative p-5 md:p-6 rounded-2xl border-2 transition-all duration-300 text-left overflow-hidden
                          ${isSelected
                            ? 'bg-gradient-to-r from-orange-500/25 via-amber-500/20 to-orange-500/25 border-orange-500 shadow-xl shadow-orange-500/30 scale-[1.02]'
                            : 'bg-white/5 border-white/10 hover:border-orange-500/50 hover:bg-white/10 hover:scale-[1.01]'
                          }
                          ${isPollEnded || userHasVoted || !user ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {/* Ripple effect */}
                        {isRippling && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-12 h-12 bg-orange-500/40 rounded-full animate-vote-ripple" />
                          </div>
                        )}

                        <div className="relative z-10 flex items-center gap-4">
                          <div className={`
                            h-8 w-8 rounded-full border-2 transition-all duration-300 flex items-center justify-center flex-shrink-0
                            ${isSelected
                              ? 'border-orange-500 bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/50'
                              : 'border-white/30'
                            }
                          `}>
                            {isSelected && (
                              <div className="h-3 w-3 rounded-full bg-white animate-scale-in-bounce" />
                            )}
                          </div>
                          <span className="font-bold text-lg md:text-xl text-white">{option.label}</span>
                          {isSelected && (
                            <Flame className="h-5 w-5 text-orange-500 animate-fire-dance ml-auto" />
                          )}
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Multi-choice Submit Button */}
            {poll.pollType === 'multiplechoice' && !shouldShowResults && selectedOptions.length > 0 && !isPollEnded && (
              <div className="animate-slide-up" style={{ animationDelay: '0.5s' }}>
                <Button
                  onClick={handleVote}
                  disabled={isVoting || !user}
                  className="w-full h-16 text-xl font-black bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 border-0 text-white relative overflow-hidden group rounded-2xl"
                  size="lg"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-amber-600 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="relative z-10 flex items-center gap-3">
                    {isVoting ? (
                      <>
                        <div className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Zap className="h-6 w-6 animate-bounce-subtle" />
                        Submit {selectedOptions.length} {selectedOptions.length === 1 ? 'Vote' : 'Votes'}
                      </>
                    )}
                  </span>
                </Button>
              </div>
            )}

            {/* Share Section */}
            <div className="flex justify-center gap-3 pt-4 animate-slide-up" style={{ animationDelay: '0.6s' }}>
              <Button
                variant="outline"
                onClick={handleShare}
                className="bg-white/5 border-white/10 hover:bg-white/10 hover:border-orange-500/30 text-white font-bold transition-all duration-300"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share Link
              </Button>
              {user && (
                <ShareToNostrDialog
                  pollQuestion={poll.question}
                  pollUrl={getPollUrl()}
                  disabled={isVoting}
                />
              )}
            </div>

            {/* Footer */}
            <div className="text-center pt-8 pb-4">
              <p className="text-sm text-white/30 flex items-center justify-center gap-2">
                Powered by{' '}
                <a
                  href="https://shakespeare.diy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500/70 hover:text-orange-500 transition-colors inline-flex items-center gap-1"
                >
                  Shakespeare
                  <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
